import React, { useEffect, useState } from 'react';
import { Modal, Upload, notification } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { getStorageService } from '../storage/storage';
import { useTranslation } from 'react-i18next';

const EXTENSIONS_BY_KIND = {
  video: ['mp4', 'mov', 'webm', 'avi', 'mkv', 'ogv'],
  audio: ['mp3', 'wav', 'm4a', 'aac', 'flac', 'ogg'],
};

const extensionOf = (name) => (name.split('.').pop() || '').toLowerCase();

// Video/audio uploader for an Object. `kind` picks which one - video and
// audio are kept as separate fields/uploaders end-to-end (backend
// videoFiles/audioFiles, mobile renders each very differently) rather than
// one mixed list sniffed by extension. Reuses antd's built-in Upload
// progress/status handling (percent bar, error state) instead of the fake
// single-spinner pattern in ImageUploader/DocumentUploader - media files are
// large enough that real per-file progress and failure feedback matter here.
// Video allows multiple files; audio is capped to one (maxCount={1} makes
// antd's Upload replace the existing file rather than add to it).
const MediaUploader = ({ kind = 'video', disabledAdd, storageFolder, fileList, setFileList }) => {
  const { t } = useTranslation();
  const allowedExtensions = EXTENSIONS_BY_KIND[kind];
  const maxCount = kind === 'audio' ? 1 : undefined;
  const [uiFileList, setUiFileList] = useState(
    (fileList || []).map(f => ({ ...f, status: 'done', response: { url: f.url } }))
  );
  const [previewFile, setPreviewFile] = useState(null);

  // fileList arrives asynchronously (SaveObject fetches the object after this
  // mounts), so the initial useState seed above is stale by the time it lands.
  // Merge-only (never replace): uiFileList is also live-updated by antd during
  // uploads, and this effect re-fires whenever our own onChange below pushes a
  // finished file up to the parent - a full replace would wipe out sibling
  // files still mid-upload in that moment.
  useEffect(() => {
    setUiFileList(prev => {
      const existingUids = new Set(prev.map(f => f.uid));
      const toAdd = (fileList || [])
        .filter(f => !existingUids.has(f.uid))
        .map(f => ({ ...f, status: 'done', response: { url: f.url } }));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });
  }, [fileList]);

  const storageService = getStorageService(storageFolder);

  const beforeUpload = (file) => {
    if (!allowedExtensions.includes(extensionOf(file.name))) {
      notification.error({
        message: t('mediaUploader.invalidFileType', 'Invalid file type'),
        description: kind === 'video'
          ? t('mediaUploader.onlyVideoAllowed', 'Only common video formats are allowed.')
          : t('mediaUploader.onlyAudioAllowed', 'Only common audio formats are allowed.'),
      });
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const customRequest = async ({ file, onProgress, onSuccess, onError }) => {
    try {
      const { originalUrl } = await storageService.uploadMedia(file, (percent) => {
        onProgress({ percent });
      });
      onSuccess({ url: originalUrl }, file);
      notification.success({
        message: t('mediaUploader.uploadSuccess', 'Upload succeeded'),
        description: file.name,
      });
    } catch (error) {
      onError(error);
      notification.error({
        message: t('mediaUploader.uploadFailed', 'Upload failed'),
        description: file.name,
      });
    }
  };

  const onChange = ({ fileList: newList }) => {
    // maxCount={1} (audio) replaces the file silently - it drops the old
    // entry from the list without ever calling onRemove, so without this its
    // storage blob would be orphaned instead of cleaned up.
    if (maxCount === 1) {
      const newUids = new Set(newList.map(f => f.uid));
      uiFileList
        .filter(f => f.status === 'done' && !newUids.has(f.uid))
        .forEach(f => storageService.deleteFiles(f.name).catch(() => {
          // ponytail: best-effort delete, same pattern as onRemove below
        }));
    }
    setUiFileList(newList);
    setFileList(newList
      .filter(f => f.status === 'done')
      .map(f => ({ uid: f.uid, name: f.name, url: f.response?.url })));
  };

  const onRemove = async (file) => {
    try {
      await storageService.deleteFiles(file.name);
    } catch (error) {
      // ponytail: best-effort delete, same pattern as GlbUploader/DocumentUploader
    }
    return true;
  };

  const onPreview = (file) => {
    const url = file.url || file.response?.url;
    if (url) setPreviewFile({ url, name: file.name });
  };

  const onDownload = (file) => {
    const url = file.url || file.response?.url;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div>
      <Upload
        fileList={uiFileList}
        customRequest={customRequest}
        beforeUpload={beforeUpload}
        onChange={onChange}
        onRemove={onRemove}
        onPreview={onPreview}
        onDownload={onDownload}
        showDownloadIcon
        multiple={kind !== 'audio'}
        maxCount={maxCount}
        disabled={disabledAdd}
        showUploadList={{
          showPreviewIcon: true,
          showDownloadIcon: true,
          showRemoveIcon: !disabledAdd,
        }}
        accept={kind === 'video' ? 'video/*' : 'audio/*'}
      >
        {!disabledAdd && (
          <button type="button" className="ant-btn" style={{ border: 0, background: 'none' }}>
            <PlusOutlined /> {kind === 'video'
              ? t('mediaUploader.selectVideo', 'Upload Video')
              : t('mediaUploader.selectAudio', 'Upload Audio')}
          </button>
        )}
      </Upload>
      <Modal
        open={!!previewFile}
        footer={previewFile?.name}
        onCancel={() => setPreviewFile(null)}
        destroyOnHidden
        centered
      >
        {previewFile && (kind === 'video'
          ? <video controls style={{ width: '100%' }} src={previewFile.url} />
          : <audio controls style={{ width: '100%' }} src={previewFile.url} />)}
      </Modal>
    </div>
  );
};

export default MediaUploader;
